import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Ce que Koffi est en train de faire — choisit la pose affichée. */
export type KoffiState = 'repos' | 'ecoute' | 'reflexion' | 'parle';

/** Poses en pied, une par état (rendu 3D de la charte café, MOKA-214). */
const KOFFI_POSES: Record<KoffiState, string> = {
  repos: 'assets/images/koffi/koffi-repos.png',
  ecoute: 'assets/images/koffi/koffi-ecoute.png',
  reflexion: 'assets/images/koffi/koffi-reflexion.png',
  parle: 'assets/images/koffi/koffi-parle.png'
};

/** Portrait serré, seul lisible à 28–32 px : en pied, le visage y fait 6 px. */
const KOFFI_AVATAR = 'assets/images/koffi/koffi-avatar.png';

/**
 * En dessous de ce côté, on passe au portrait dans une pastille ronde : la pose
 * en pied devient une silhouette grise sans visage.
 */
const COMPACT_MAX_SIZE = 48;

/**
 * Koffi, la mascotte (la tasse de café de la charte, MOKA-214), qui incarne
 * l'assistant. Composant unique pour les deux usages : la mascotte
 * d'accueil (grande, animée, une pose par état) et la vignette devant chaque
 * réponse dans le fil (petite, immobile, portrait rond).
 *
 * Images PNG plutôt que SVG : le rendu 3D des mascottes ne se dessine pas en
 * tracés. La contrepartie, c'est que les couleurs ne suivent plus le design
 * system — le métal argenté tient sur fond clair comme sombre, et le halo,
 * lui, reste en `--primary`.
 */
@Component({
  selector: 'app-voice-robot',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './voice-robot.component.html',
  styleUrl: './voice-robot.component.scss',
  host: {
    '[class.voice-robot--animated]': 'animated()',
    '[class.voice-robot--compact]': 'compact()',
    '[attr.data-state]': 'state()',
    '[style.--robot-size.px]': 'size()'
  }
})
export class VoiceRobotComponent {
  /** Côté du carré, en pixels. */
  size = input(84);

  /**
   * Animations (flottement, halo, pulsation). Désactivées dans le fil :
   * dix vignettes qui respirent en même temps transformeraient une
   * conversation en aquarium.
   */
  animated = input(true);

  /** État courant de l'assistant : choisit la pose (repos par défaut). */
  state = input<KoffiState>('repos');

  protected readonly label = "Koffi, l'assistant Mokah+";
  protected readonly avatarSrc = KOFFI_AVATAR;
  protected readonly states = Object.keys(KOFFI_POSES) as KoffiState[];
  protected readonly poses = KOFFI_POSES;

  protected compact = computed(() => this.size() <= COMPACT_MAX_SIZE);
}
